import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { BehaviorSubject, Subject, timer } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { v4 } from 'uuid';
import { environment } from '../../../environments';
import {
  ITrackingAction,
  ITrackingObject,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import { WindowRefService } from 'i2g-ng-utils';
import { IconSizesEnum, IIcon } from 'i2g-ng-ui';

export interface IOauthGoogleInfo {
  email: string;
  idToken: string;
}

@Component({
  selector: 'app-oauth-google-circle',
  templateUrl: 'oauth-google-circle.component.html',
  styleUrls: ['oauth-google-circle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OauthGoogleCircleComponent implements OnInit, OnDestroy {
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAction: ITrackingAction;
  @Input() trackingAdditionalProperties: ITrackingProperties;

  @Output() authenticationSuccess = new EventEmitter<IOauthGoogleInfo>();
  @Output() authenticationError = new EventEmitter<Error>();

  googleIcon: IIcon;
  id: string;
  initialized$ = new BehaviorSubject(false);
  processing$ = new BehaviorSubject(false);

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _googleAuth: any;

  constructor(
    private _windowRefService: WindowRefService,
    private _ngZone: NgZone
  ) {
    this.id = v4();
  }

  ngOnInit() {
    this.googleIcon = {
      name: 'google',
      size: IconSizesEnum.custom,
    };

    const win = this._windowRefService.nativeWindow;
    timer(0, 1000)
      .pipe(
        takeUntil(this._destroyed),
        map(() => win.gapi),
        filter((x) => !!x),
        take(1)
      )
      .subscribe((gapi) => {
        gapi.load('auth2', () => {
          this._ngZone.run(() => {
            // runs the callback in the zone
            // see:
            // https://stackoverflow.com/questions/35530483/google-sign-in-for-websites-and-angular-2-using-typescript
            this._googleAuth = gapi.auth2.init({
              client_id: environment.googleClientId,
              fetch_basic_profile: true,
              scope: 'email',
              prompt: 'select_account',
            });
            this.initialized$.next(true);
          });
        });
      });
  }

  signInToGoogle() {
    this.processing$.next(true);

    this._googleAuth
      .signIn()
      .then((googleUser) => {
        this._ngZone.run(() => {
          // runs the callback in the zone
          // see:
          // https://stackoverflow.com/questions/35530483/google-sign-in-for-websites-and-angular-2-using-typescript
          const email = googleUser.getBasicProfile().getEmail();
          const idToken = googleUser.getAuthResponse().id_token;
          this.authenticationSuccess.emit({ email, idToken });
          this.processing$.next(false);
        });
      })
      .catch((error) => {
        this._ngZone.run(() => {
          // runs the callback in the zone
          // see:
          // https://stackoverflow.com/questions/35530483/google-sign-in-for-websites-and-angular-2-using-typescript
          this.authenticationError.emit(error);
          this.processing$.next(false);
        });
      });
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
